import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { authFetch } from '@/api/client'

/** One font available to the text overlay. ``family`` is the file stem in
 * the backend's ``data/fonts/`` — the same string the burn-in resolves
 * back to a file, which is what keeps preview and export on one typeface. */
export interface FontOption {
  family: string
  file: string
}

const FONTS_QUERY_KEY = ['tools', 'fonts'] as const

/** Families already handed to the browser, so a re-render or a second
 * mount doesn't re-download and re-register them. */
const registered = new Set<string>()

/**
 * Fonts the editor can draw with, registered with the browser.
 *
 * The files sit behind ``/api/v1`` auth, so a plain ``@font-face`` URL
 * would 401 — the bytes are fetched with the session token and turned into
 * a ``FontFace`` from the ArrayBuffer instead.
 *
 * A font that fails to load is dropped from the returned list rather than
 * left in the picker: offering a family the browser can't draw would show
 * a fallback in the preview and the real thing in the export, which is the
 * exact divergence this whole path exists to prevent.
 */
export function useFonts() {
  const query = useQuery({
    queryKey: FONTS_QUERY_KEY,
    queryFn: async (): Promise<FontOption[]> => {
      const res = await authFetch('/api/v1/tools/fonts', { credentials: 'same-origin' })
      if (!res.ok) return []
      const body = (await res.json()) as { data: FontOption[] }
      return body.data
    },
    // The set only changes when someone drops a file in data/fonts/.
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    const fonts = query.data
    if (!fonts || typeof document === 'undefined') return
    // AbortController rather than a boolean flag: TS narrows a local
    // `let` to `false` and then calls the unmount check dead code.
    const aborted = new AbortController()

    void (async () => {
      for (const font of fonts) {
        if (registered.has(font.family)) continue
        registered.add(font.family)
        try {
          const res = await authFetch(`/api/v1/tools/fonts/${encodeURIComponent(font.family)}`, {
            credentials: 'same-origin',
          })
          if (!res.ok) throw new Error(`HTTP ${String(res.status)}`)
          const buffer = await res.arrayBuffer()
          if (aborted.signal.aborted) return
          const face = new FontFace(font.family, buffer)
          await face.load()
          document.fonts.add(face)
        } catch {
          // Let it be retried on the next mount instead of poisoning the
          // set for the whole session.
          registered.delete(font.family)
        }
      }
    })()

    return () => {
      aborted.abort()
    }
  }, [query.data])

  return query
}
